import clsx from 'clsx'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
    selectUpdateNotification,
    setUpdateNotification,
} from '../../../features/notifications/notificationsSlice'
import { useTabs } from '../../../hooks/useTabs'
import {
    useCreateNotificationMutation,
    useGetAllNotificationQuery,
    useGetNotificationByIdQuery,
    useUpdateNotificationMutation,
} from '../../../services/notifications/notifications'
import type { ResponseType } from '../../../services/types'
import type { INotification } from '../../../types/notifications'
import Modal from '../../common/Modal'

import { GeneralSettingsTab } from './GeneralSettingsTab'
import { NotificationTab } from './NotificationTab'
import TargetTab from './TargetTab'

interface AddNotificationModalProps {
    isOpen: boolean
    onClose: () => void
    notificationId?: number
}

const tabs = [
    { id: 'general_settings', label: 'General Settings' },
    { id: 'target', label: 'Target' },
    { id: 'notifications', label: 'Notification' },
]

const AddNotificationModal: React.FC<AddNotificationModalProps> = ({
    isOpen,
    onClose,
    notificationId = 0,
}) => {
    const { activeTab, setActiveTab } = useTabs(tabs)
    const [createNotification] = useCreateNotificationMutation()
    const [updateNotification] = useUpdateNotificationMutation()
    const { isLoading } = useGetNotificationByIdQuery(notificationId, {
        skip: !notificationId,
    })
    const dispatch = useDispatch()
    const formState = useSelector(selectUpdateNotification)
    const { refetch } = useGetAllNotificationQuery('')

    const handleSave = async () => {
        let res: ResponseType<INotification>

        const { status, ...rest } = formState

        if (notificationId === 0) {
            res = await createNotification(formState).unwrap()
        } else {
            res = await updateNotification({
                id: notificationId,
                body: rest,
            }).unwrap()
        }

        if (res?.success) {
            dispatch(setUpdateNotification({}))
            toast.success('Notification created successfully')
            refetch()
            onClose()
        } else {
            toast.error(res?.message || 'Something went wrong')
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add Notification">
            <div className="modal__body p-0">
                <div className="tabs flex-auto">
                    <div className="tabs-links-list modal__tabs__body">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={clsx(
                                    'btn full-radius btn--xs btn-tab-custom',
                                    { 'active-tab': activeTab === tab.id },
                                )}
                            >
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>
                    <div className="tabs-body flex-auto">
                        {activeTab === 'general_settings' && (
                            <GeneralSettingsTab />
                        )}
                        {activeTab === 'target' && <TargetTab />}
                        {activeTab === 'notifications' && <NotificationTab />}
                    </div>
                </div>
                <div className="custom-form-footer justify-end">
                    <div className="flex-group flex-group--xs">
                        <button
                            onClick={handleSave}
                            className="btn btn--lg full-radius btn--primary-gradient"
                        >
                            <span>Add Notification</span>
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default AddNotificationModal
